/**
 * Whether an axios error means the signed-in session expired — as opposed to
 * a 401 on a request that never carried a token, which is a normal answer for
 * pre-auth calls (the login page's /v2/apps/bootstrap fetch). Treating those
 * as "expired" made http.js logout + reload on the login page, which reloads
 * the login page forever.
 *
 * withAuth() always stamps a header, so "did it carry a token" has to be read
 * out of the *value*, not its presence. `http.js:113` interpolates
 * `state.user ? state.user.access_token.accessToken : null`, which yields
 * three different empty shapes depending on how empty the store is:
 * `Bearer null` (no `user` at all), `Bearer undefined` (a `user` whose
 * `access_token` is `{}` — the common one) and `Bearer ` (an empty string).
 *
 * SAM-145. This used to exclude only the literal `Bearer null`, i.e. the
 * rarest of the three, so an anonymous caller's perfectly normal 401 read as
 * an expired session and http.js logged out and called
 * `window.location.reload()`. On the public questionnaire
 * (`/answer/:id/:project_id`) that is the reported "resets itself partway
 * through" — the reload discards whatever had been answered. Matching on the
 * token rather than on a list of literals is what makes this a root-cause
 * fix: a fourth empty shape cannot reintroduce the bug.
 *
 * Kept dependency-free so it is unit-testable without the store/axios setup.
 */
const EMPTY_TOKENS = ["", "null", "undefined"];

export function isExpiredSessionError(error) {
  if (!error || !error.response || error.response.status !== 401) return false;
  const auth =
    error.config && error.config.headers && error.config.headers.Authorization;
  if (!auth) return false;
  const token = String(auth).replace(/^Bearer\b/i, "").trim();
  return !EMPTY_TOKENS.includes(token);
}
